import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import type { NotionConnection, NotionSetupInfo } from "../../../../api/client.ts";
import ConfirmDialog from "../../../../components/ConfirmDialog.tsx";
import NotionLogo from "../../../../components/NotionLogo.tsx";
import AccordionCard from "../../../../components/AccordionCard.tsx";
import RelativeTime from "../../../../components/RelativeTime.tsx";
import StatusIcon, { type Status, StatusLabel } from "../../../../components/StatusIcon.tsx";

interface NotionIntegrationSectionProps {
  setupInfo: NotionSetupInfo | undefined;
  connections: NotionConnection[] | undefined;
  isLoading: boolean;
  // Organization admins (org:manage_credentials) connect, scope and disconnect.
  canManage: boolean;
  deletingId: string | null;
  defaultExpanded?: boolean;
  onConnect: () => void;
  onDisconnect: (connection: NotionConnection) => void;
  onOpenScopes: (connection: NotionConnection) => void;
}

// Notion's icon is an emoji or an image URL.
function WorkspaceIcon({ icon }: { icon: string | null }) {
  if (!icon) return null;
  if (/^https?:\/\//.test(icon)) {
    return <Box component="img" src={icon} alt="" sx={{ width: 24, height: 24, borderRadius: 0.5 }} />;
  }
  return <Typography sx={{ fontSize: 20, lineHeight: 1 }}>{icon}</Typography>;
}

export default function NotionIntegrationSection({
  setupInfo,
  connections,
  isLoading,
  canManage,
  deletingId,
  defaultExpanded,
  onConnect,
  onDisconnect,
  onOpenScopes,
}: NotionIntegrationSectionProps) {
  const [confirming, setConfirming] = useState<NotionConnection | null>(null);
  const list = connections ?? [];

  const broken = list.filter((c) => c.status === "needs_reconnect").length;
  let status: Status;
  if (isLoading) {
    status = { kind: "loading" };
  } else if (broken > 0) {
    status = {
      kind: "attention",
      label: list.length === 1 ? "Needs reconnect" : `${broken} of ${list.length} need reconnect`,
    };
  } else if (list.length > 0) {
    status = { kind: "ok", label: list.length === 1 ? "Connected" : `${list.length} connected` };
  } else if (setupInfo && !setupInfo.configured) {
    status = { kind: "off", label: "Not available" };
  } else {
    status = { kind: "off", label: "Not connected" };
  }

  return (
    <AccordionCard
      logo={<NotionLogo />}
      title="Notion"
      summary={
        list.length
          ? list.map((c) => c.workspace_name || c.workspace_id).join(", ")
          : "Connect your organization's Notion workspace, then pick the pages each tenant's workflows watch and its agents may read."
      }
      status={status}
      defaultExpanded={broken > 0 || defaultExpanded}
    >
      <Stack spacing={2}>
        {setupInfo && !setupInfo.configured && (
          <Alert severity="info">
            The Notion app is not set up on this platform yet, so workspaces can't be connected.
            A developer registers a public Notion integration with redirect URI{" "}
            <code>{setupInfo.redirect_uri}</code> and webhook URL <code>{setupInfo.webhook_url}</code>,
            then sets <code>NOTION_CLIENT_ID</code> and <code>NOTION_CLIENT_SECRET</code> on the backend.
          </Alert>
        )}
        {list.map((c) => {
          const needsReconnect = c.status === "needs_reconnect";
          const name = c.workspace_name || c.workspace_id;
          return (
            <Box key={c.id}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
                  <WorkspaceIcon icon={c.workspace_icon} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1">
                      Connected to <strong>{name}</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      {c.last_polled_at ? (
                        <>
                          Last synced <RelativeTime value={c.last_polled_at} />
                        </>
                      ) : (
                        "Not synced yet"
                      )}
                    </Typography>
                  </Box>
                  {needsReconnect ? (
                    <StatusLabel kind="attention" label="Needs reconnect" />
                  ) : (
                    <StatusIcon kind="ok" label="Active" />
                  )}
                </Stack>
                {canManage && (
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={() => onOpenScopes(c)}>
                      Pages for tenants
                    </Button>
                    {needsReconnect && setupInfo?.configured && (
                      <Button variant="contained" onClick={onConnect}>
                        Reconnect
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={deletingId === c.id}
                      onClick={() => setConfirming(c)}
                    >
                      Disconnect
                    </Button>
                  </Stack>
                )}
              </Stack>
              {needsReconnect && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Vayken can no longer read this workspace
                  {c.last_error ? ` (${c.last_error})` : ""}. Reconnect it to resume syncing; the
                  pages chosen for each tenant are kept.
                </Alert>
              )}
            </Box>
          );
        })}
        {canManage ? (
          setupInfo?.configured && (
            <Box>
              <Button variant={list.length ? "outlined" : "contained"} onClick={onConnect}>
                {list.length ? "Connect another workspace" : "Connect Notion"}
              </Button>
            </Box>
          )
        ) : (
          list.length === 0 && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No Notion workspace connected. Organization admins can connect one.
            </Typography>
          )
        )}
      </Stack>

      <ConfirmDialog
        open={!!confirming}
        title="Disconnect Notion"
        confirmLabel="Disconnect"
        onConfirm={() => {
          if (confirming) onDisconnect(confirming);
          setConfirming(null);
        }}
        onClose={() => setConfirming(null)}
      >
        Disconnect <strong>{confirming?.workspace_name || confirming?.workspace_id}</strong>?
        Vayken deletes what it holds of this workspace: the page index, page revisions and every
        tenant's chosen pages, and clears evaluator prompts that quoted Notion content. Signals,
        research briefs, agent transcripts and Slack posts already made from Notion pages are kept.
      </ConfirmDialog>
    </AccordionCard>
  );
}
